import { PaymentAction } from '@adyen/adyen-web/dist/types/types';
import { useCallback, useEffect, useRef, useState } from 'react';

import ActionCreator from '@components/ActionCreator/ActionCreator';
import { BancontactMobileDetails, Details, ThreeDSDetails } from '@hooks/useActionCreator';
import { useTracking } from '@hooks/useTracking';
import { useConfig } from '@providers/ConfigProvider';
import AdobeTrackingService from '@services/AdobeTrackingService';
import { Environment, Locale, PaymentMethod, ShopBundle } from '@types-internal';
import {
  handleActionCreatorPendingRedirect,
  ACTION_CREATOR_FAILED_REDIRECT_URL,
  FAILED_QR_CODE_REDIRECT_URL,
  handleQrCodeFailureRedirect,
} from '@utils/redirect';
import { convertRunningEnvToAdyenEnv } from '@utils/utils';

export type PaymentAuthorizationActionProps = {
  shopBundle: ShopBundle;
  locale: Locale;
  paymentMethod: PaymentMethod;
  csrfToken: string;
  paymentId: string;
  paymentAction: PaymentAction;
  env: Environment;
  customerId?: string;
  storeCardDetails: boolean;
  processPaymentDetailsUrl: string;
  isMobileApp: boolean;
  currency?: string;
  sid?: string;
  siteId?: number;
  customerType?: string;
};

const ShopCheckoutActionCreator = ({
  shopBundle,
  locale,
  csrfToken,
  paymentAction,
  paymentId,
  paymentMethod,
  storeCardDetails,
  processPaymentDetailsUrl,
  isMobileApp,
  currency,
  customerId,
  sid,
  siteId,
  customerType,
}: PaymentAuthorizationActionProps) => {
  const {
    config: {
      adyen: { clientKey },
      env,
    },
  } = useConfig();

  const track = useTracking({
    currency,
    customerId,
    sid,
    siteId,
    guest: customerType === 'GUEST_CUSTOMER',
  });
  const formRef = useRef<HTMLFormElement>(null);
  const [detailsPayload, setDetailsPayload] = useState<string>('');
  const [detailsThreeDSResult, setDetailsThreeDSResult] = useState<string>('');

  const handlePaymentDetails = useCallback((details: Details) => {
    if (paymentAction.type === 'qrCode')
      setDetailsPayload((details as BancontactMobileDetails).payload);
    if (paymentAction.type === 'threeDS2')
      setDetailsThreeDSResult((details as ThreeDSDetails).threeDSResult);
  }, []);

  const actionTimedOutHandler = useCallback(() => {
    if (isMobileApp) handleActionCreatorPendingRedirect();
    else handleQrCodeFailureRedirect();
  }, []);

  useEffect(() => {
    if (!!(detailsPayload || detailsThreeDSResult) && formRef && formRef.current) {
      formRef.current.submit();
      if (detailsThreeDSResult) {
        if (track) {
          track('3DSsubmitOK');
        } else {
          AdobeTrackingService.adyenTracking('3DSsubmitOK');
        }
      }
    }
  }, [detailsPayload, detailsThreeDSResult]);

  return (
    <>
      <ActionCreator
        paymentAction={paymentAction}
        clientKey={clientKey}
        adyenEnvironment={convertRunningEnvToAdyenEnv(env)}
        locale={locale}
        sendPaymentDetailsHandler={handlePaymentDetails}
        actionTimedOutHandler={actionTimedOutHandler}
        shopBundle={shopBundle}
        invalidQRCodeActionShopUrl={
          isMobileApp ? ACTION_CREATOR_FAILED_REDIRECT_URL : FAILED_QR_CODE_REDIRECT_URL
        }
        paymentId={paymentId}
      />
      <form ref={formRef} action={processPaymentDetailsUrl} method="post" noValidate>
        <input type="hidden" name="csrfToken" value={csrfToken} />
        {paymentAction.type === 'qrCode' && (
          <input type="hidden" name="payload" value={detailsPayload} />
        )}
        {paymentAction.type === 'threeDS2' && (
          <input type="hidden" name="threeDSResult" value={detailsThreeDSResult} />
        )}
        <input type="hidden" name="paymentId" value={paymentId} />
        <input type="hidden" name="paymentMethod" value={paymentMethod} />
        <input type="hidden" name="customerId" value={customerId} />
        <input type="hidden" name="storeCardDetails" value={storeCardDetails.toString()} />
        <input type="hidden" name="isMobileApp" value={isMobileApp.toString()} />
      </form>
    </>
  );
};

export default ShopCheckoutActionCreator;
